import React, { FC, useMemo } from 'react';
import './GenericDomainDetail.css';
import {
	Alert, PageLoader,
	ActionStatusHelper, FeedBackType,
	DetailsBox, DetailsListBox,
	MessageHelper, GenricDomainStack, useBreakPoint,
	DetailSection,
	DomainType,
	MediaBox,
	useUIFeedback,
	UIFeedback,
	useDomainDetail,
	UIButton
} from '@/Shared';

import { AccessRightModel } from '@/Domains';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';
import { ModalHook } from '@/Shared/Logic/Hooks/TechnicalHooks/useModal';

export interface DetailActionParams<T> {
	breakPoint: any,
	entityVM: T;
	goToForm: (domainType: DomainType, id?: number, formType?: string) => void;
	accessRight: AccessRightModel;
}


interface GenericDomainDetailProps {
	domainType: DomainType;
	inModalHook?: ModalHook;
	displayStackMenu?: boolean;
	extraArgs?: any
}


/**
 * =====================================================
 * GenericDomainDetail
 * =====================================================
 *
 * Generic detail page renderer for all domain types.
 *
 * Responsibilities:
 * - Orchestrates domain-specific detail hooks and UI feedback.
 * - Renders full-page or modal-based detail views.
 * - Handles loading and error states.
 * - Displays header actions, sub-actions, sections, and media.
 *
 * Notes:
 * - Domain-specific behavior is provided via DOMAIN_CONFIGS.
 * - Business logic remains inside domain hooks and view models.
 *
 * Author: Samano CASTRE
 * Date: July 16, 2025
 */

const GenericDomainDetail: FC<GenericDomainDetailProps> = ({
	domainType,
	displayStackMenu = true,
	inModalHook,
	extraArgs
}) => {
	const { isDesktop } = useBreakPoint();
	const uiFeedbackHooks = useUIFeedback();
	const { domainLabel, globalButtons } = DOMAIN_CONFIGS[domainType];

	const {
		entityVM,
		response,
		UIActions
	} = useDomainDetail(
		domainType,
		uiFeedbackHooks,
		inModalHook,
		extraArgs
	);


	const medias = useMemo(() => {
		return entityVM?.getMediaItems?.() || { images: [], videos: [] };
	}, [entityVM]);


	/** Global loading and error handling */
	const { isLoading, isFailed } = ActionStatusHelper.is(response?.status);

	if (isFailed) {
		return (
			<Alert
				data={{
					message: response?.error || 'Une erreur est survenue',
					type: FeedBackType.ERROR
				}}
			/>
		);
	}

	if (isLoading || !entityVM?.getAllSections) {
		return (
			<PageLoader
				message={MessageHelper.FETCHING.LOADING(domainLabel)}
			/>
		);
	}


	/** Detail sections and media items provided by the view model */
	const allSections: DetailSection[] = entityVM.getAllSections();


	/** Derived UI flags for conditional rendering */
	const shouldDisplayStack = !inModalHook && isDesktop && displayStackMenu;
	const shouldDisplayImages = medias.images.length > 0;
	const shouldDisplayVideos = medias.videos.length > 0;
	const hasSubActions = globalButtons.length > 0;
	const lightBoxHook = uiFeedbackHooks.lightBoxHook;

	if(shouldDisplayImages && shouldDisplayVideos && !lightBoxHook) {
		return <Alert data={{
			title : MessageHelper.TECHNICAL.MISSING_HOOK.TITLE,
			message : MessageHelper.TECHNICAL.MISSING_HOOK.MESSAGE,
			type : FeedBackType.ERROR
		}} />
	}

	return (
		<div className={`GenericDomainDetail fade-in ${domainType}Detail`}>
			{
				shouldDisplayStack && (
					<GenricDomainStack
						activeKey={domainType}
						format='List' />
				)
			}

			<div className='details-page'>

				<div className="details-page__header">
					<h3 className='ellipsis' title={entityVM.title}>{entityVM.title}</h3>
					<div className="fixed-header-actions flex-row">
						{
							UIActions.header.map( action =>
								<UIButton
									key={action.key}
									action={action}
									isDesktop={isDesktop}
								/>
							)
						}
					</div>
				</div>

				<div className="details-page__body sm-scroll">
					{hasSubActions && <div className="sub-actions flex-row">
						{ UIActions.sub.map(action =>
								<UIButton
									key={action.key}
									action={action}
									isDesktop={isDesktop}
								/>
						)}
					</div>}

					<div className="fieldset-wrapper">
						{/**
						 * Global UI feedback layers
						 *
						 * These components are mounted once at the detail level
						 * and are triggered imperatively by domain logic.
						 */}
						<UIFeedback uiFeedbackHooks={uiFeedbackHooks} />

						{shouldDisplayImages && lightBoxHook && (
							<MediaBox
								legend='Images'
								mediaItems={medias.images}
								type='image'
								onDelete={
									(mediaId: number) => UIActions.media?.[0].onClick?.(mediaId)
								}
								lightBoxHook={lightBoxHook}
							/>
						)}
						{shouldDisplayVideos && lightBoxHook && (
							<MediaBox
								legend='Vidéos'
								mediaItems={medias.videos}
								type='video'
								onDelete={
									(mediaId: number) => UIActions.media?.[0].onClick?.(mediaId)
								}
								lightBoxHook={lightBoxHook}
							/>
						)}

						{allSections.map((section, index) => {
							const key = `${section.type}-${index}`;
							return section.type === 'box'
								? (<DetailsBox
									key={key}
									data={section.sectionFn() as any}
									legend={section.legend}
								/>
								)
								: (<DetailsListBox
									key={key}
									data={section.sectionFn() as any}
									legend={section.legend}
								/>
								)
						}
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default GenericDomainDetail;